import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles, withStyles } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';

// Styles / Components
import { setModalIsOpened } from '@/main/store/modal';
import { SpinnerLoading } from '../shared/styles';
import * as S from './styles';
// Redux

const styles = (theme: Theme) => ({
  dialogHeader: {
    margin: 0,
    backgroundColor: theme.palette.common.black,
    padding: 10,
    color: theme.palette.common.white,
    minHeight: 40
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.common.white
  },
  dialogTitle: {
    fontSize: 14,
    fontWeight: 'bold'
  }
});
const MakeStylesDialog = makeStyles((theme: Theme) => ({
  stylesCustomHere: {
    '& .MuiPaper-root': {
      width: '100%',
      maxWidth: ({ width }: { width: number }) => width,
      [theme.breakpoints.up('xl')]: {
        width: '100%',
        maxWidth: ({ width, isResponsiveXL }: { width: number, isResponsiveXL: boolean }) => isResponsiveXL ? '80% !important' : width,
      }
    }
  }
}));
const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.dialogHeader}
      {...other}>
      <span className={classes.dialogTitle}>{children}</span>
      {onClose && !props.disabledCloseIcon ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  }
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
  root: {
    margin: 0,
    padding: theme.spacing(1)
  }
}))(MuiDialogActions);

interface Props {
  handleClose: () => void;
  content: any;
  open: boolean;
  title: string;
  hasActionConfirm: boolean;
  hasActionCancel: boolean;
  width?: number;
  labelActionConfirm?: string;
  onClickActionConfirm?: () => Promise<void> | void;
  labelActionCancel?: string;
  onClickActionCancel?: () => Promise<void> | void;
  disabledCloseIcon?: boolean;
  isLoadingActions?: boolean;
  size?: 'small' | 'medium' | 'large' | 'extraLarge';
  isResponsiveXL?: boolean;
}

export default function KogModalComponent({
  open,
  disabledCloseIcon = false,
  handleClose,
  title,
  content,
  width,
  hasActionConfirm,
  hasActionCancel,
  labelActionConfirm,
  labelActionCancel,
  isLoadingActions = false,
  onClickActionConfirm,
  onClickActionCancel,
  size = 'medium',
  isResponsiveXL = false
}: Props) {
  const [typeActionClick, setTypeActionClick] = useState<'confirm' | 'cancel' | null>(null)

  const dispatch = useDispatch();
  const optionsSize = {
    small: {
      width: 400
    },
    medium: {
      width: 600
    },
    large: {
      width: 800
    },
    extraLarge: {
      width: 1200
    }
  }
  const defaultWidth: number = optionsSize[size].width;
  const hasActionsModal = () => (hasActionConfirm || hasActionCancel);
  const getWidthModal = () => width || defaultWidth;
  const { stylesCustomHere } = MakeStylesDialog({ width: getWidthModal(), isResponsiveXL });

  function handleModalClose() {
    if (disabledCloseIcon || isLoadingActions) return
    dispatch(setModalIsOpened(false, null));
    handleClose();
  }
  async function asyncConfirmAction() {
    if (onClickActionConfirm) {
      setTypeActionClick('confirm');
      await onClickActionConfirm();
      handleModalClose();
      setTypeActionClick(null);
      return
    }
    handleModalClose();
  }
  async function asyncCancelAction() {
    if (onClickActionCancel) {
      setTypeActionClick('cancel');
      await onClickActionCancel();
      handleModalClose();
      setTypeActionClick(null);
      return
    }
    handleModalClose();
  }

  useEffect(() => {
    if (open) {
      dispatch(setModalIsOpened(open, title));
    }
  }, [open]);

  return (
    <div>
      <Dialog
        onClose={handleModalClose}
        open={open}
        className={stylesCustomHere}
      >
        <DialogTitle
          disabledCloseIcon={disabledCloseIcon || isLoadingActions}
          onClose={handleModalClose}>
          {title}
        </DialogTitle>
        <DialogContent dividers>{content}</DialogContent>
        {hasActionsModal() && (
          <DialogActions>
            {hasActionCancel && (
              <S.ButtonActionCancel
                id="btn-action-cancel"
                disabled={isLoadingActions}
                onClick={() => asyncCancelAction()}
              >
                <S.TextButtonAction>
                  {labelActionCancel || 'Cancel'}
                  {(isLoadingActions && typeActionClick === 'cancel') && <SpinnerLoading size='xsmall' />}
                </S.TextButtonAction>
              </S.ButtonActionCancel>
            )}
            {hasActionConfirm && (
              <S.ButtonActionConfirm
                id="btn-action-confirm"
                disabled={isLoadingActions}
                onClick={() => asyncConfirmAction()}
              >
                <S.TextButtonAction>
                  {labelActionConfirm || 'Confirm'}
                  {(isLoadingActions && typeActionClick === 'confirm') && <SpinnerLoading size='xsmall' />}
                </S.TextButtonAction>
              </S.ButtonActionConfirm>
            )}
          </DialogActions>
        )}
      </Dialog>
    </div>
  );
}
